// src/comparisons/ComparisonLayout.js
import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  Alert,
  AlertTitle,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Assignment as AssignmentIcon,
  Dashboard as DashboardIcon,
} from '@mui/icons-material';
import api from '../config/api';
import useStore from '../config/store';
import ComparisonResults from './ComparisonResult';
import ComparisonSetup from './ComparisonSetup';
import ComparisonDashboard from './ComparisonDashboard';
import { useTranslation } from 'react-i18next';

export default function ComparisonLayout() {
  const { authToken, showAlert, project, organization, language } = useStore.getState();

  // Estados principales
  const [tabValue, setTabValue] = useState(0);
  const [currentStep, setCurrentStep] = useState('setup'); // 'setup' | 'results'
  const [positions, setPositions] = useState([]);
  const [targetPosition, setTargetPosition] = useState(null);
  const [applicantReports, setApplicantReports] = useState([]);
  const [groupedApplicants, setGroupedApplicants] = useState({});
  const [selectedReports, setSelectedReports] = useState({});
  const [comparisonMode, setComparisonMode] = useState('');
  const [idealApplicant, setIdealApplicant] = useState(null);
  const [loading, setLoading] = useState({
    positions: false,
    reports: false,
    comparison: false,
  });

  // Estados para filtros de postulantes
  const [applicantFilter, setApplicantFilter] = useState('');
  const [showOnlyTargetPosition, setShowOnlyTargetPosition] = useState(false);

  // Estados para la comparación
  const [currentComparison, setCurrentComparison] = useState(null);
  const [comparisonError, setComparisonError] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);

  // Estados para verificación de límites de uso
  const [usageCheck, setUsageCheck] = useState(null);
  const [loadingUsageCheck, setLoadingUsageCheck] = useState(true);

  const { t } = useTranslation();

  // Cargar positions al inicio
  useEffect(() => {
    loadPositions();
    loadAllApplicantReports();
  }, []);

  // Agrupar applicants cuando cambian los reportes
  useEffect(() => {
    if (applicantReports.length > 0) {
      groupApplicantsByName();
    }
  }, [applicantReports]);

  // Cleanup del polling cuando el componente se desmonta
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  // Verificar límites de uso al cargar el componente
  useEffect(() => {
    if (organization && authToken) {
      checkUsageLimits();
    }
  }, [organization]);

  const loadPositions = async () => {
    try {
      setLoading(prev => ({ ...prev, positions: true }));
      const { data } = await api.get('/positions', {
        params: { projectId: project },
        headers: { Authorization: `Bearer ${authToken}` },
      });

      if (data.response && data.response.status_response === 'OK') {
        setPositions(data.response.data || []);
      } else {
        showAlert(t('positions.loadError'), 'error');
      }
    } catch (error) {
      console.error('Error al cargar positions:', error);
      showAlert(t('generalApiError'), 'error');
    } finally {
      setLoading(prev => ({ ...prev, positions: false }));
    }
  };

  const loadAllApplicantReports = async () => {
    try {
      setLoading(prev => ({ ...prev, reports: true }));

      const { data } = await api.get('/reports', {
        params: { projectId: project },
        headers: { Authorization: `Bearer ${authToken}` },
      });

      if (data.data) {
        const reportsWithData = data.data.filter(report =>
          report.structured_data &&
          Object.keys(report.structured_data).length > 0 &&
          report.status === 'completado'
        );
        setApplicantReports(reportsWithData);
      } else {
        setApplicantReports([]);
      }
    } catch (error) {
      console.error('Error al cargar reportes:', error);
      showAlert(t('comparisonDashboard.reportsLoadError'), 'error');
      setApplicantReports([]);
    } finally {
      setLoading(prev => ({ ...prev, reports: false }));
    }
  };

  const groupApplicantsByName = () => {
    const grouped = {};

    applicantReports.forEach(report => {
      const applicantKey = report.applicant_name || report.applicant_id || t('comparisonDashboard.noName');

      if (!grouped[applicantKey]) {
        grouped[applicantKey] = {
          applicantName: applicantKey,
          applicantEmail: report.applicant_email,
          applicantId: report.applicant_id,
          reports: [],
        };
      }

      const reportWithPosition = {
        ...report,
        originalPosition: report.position_name || t('comparisonDashboard.noPosition'),
        originalPositionId: report.position_id,
      };

      grouped[applicantKey].reports.push(reportWithPosition);
    });

    Object.keys(grouped).forEach(key => {
      grouped[key].reports.sort((a, b) => {
        const dateA = new Date(a.created_at?._seconds * 1000 || 0);
        const dateB = new Date(b.created_at?._seconds * 1000 || 0);
        return dateB - dateA;
      });
    });

    setGroupedApplicants(grouped);
    setSelectedReports({});
  };

  // Función para verificar límites de uso
  const checkUsageLimits = async () => {
    if (!organization || !authToken) return;

    try {
      setLoadingUsageCheck(true);
      const { data } = await api.get(
        `/organizations/${organization}/usage/check`,
        {
          params: { action: 'comparisons' },
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      if (data.response && data.response.status_response === "OK") {
        setUsageCheck(data.response);
      } else {
        console.error("Error al verificar límites de uso:", data);
        setUsageCheck({ canPerform: false });
      }
    } catch (error) {
      console.error("Error al verificar límites de uso:", error);
      setUsageCheck({ canPerform: true }); // En caso de error, permitir uso
    } finally {
      setLoadingUsageCheck(false);
    }
  };

  const handleReportSelection = (applicantKey, reportId) => {
    setSelectedReports(prev => {
      const newSelection = { ...prev };

      if (newSelection[applicantKey] === reportId) {
        delete newSelection[applicantKey];
      } else {
        newSelection[applicantKey] = reportId;
      }

      return newSelection;
    });
  };

  const handleComparisonModeChange = (mode) => {
    setComparisonMode(mode);
    if (mode !== '1vsAll') {
      setIdealApplicant(null);
    }
  };

  const handleIdealApplicantSelection = (applicantKey) => {
    if (!selectedReports[applicantKey]) {
      showAlert(t('comparisonDashboard.selectReportForApplicant'), 'warning');
      return;
    }

    setIdealApplicant(applicantKey === idealApplicant ? null : applicantKey);
  };

  const formatDate = (timestamp) => {
    if (!timestamp?._seconds) return t('dateNotAvailable');
    const date = new Date(timestamp._seconds * 1000);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const canStartComparison = () => {
    const hasTargetPosition = targetPosition !== null;
    const hasSelectedReports = Object.keys(selectedReports).length >= 2;
    const hasComparisonMode = comparisonMode !== '';
    const hasIdealIfNeeded = comparisonMode !== '1vsAll' || idealApplicant !== null;

    return hasTargetPosition && hasSelectedReports && hasComparisonMode && hasIdealIfNeeded;
  };

  const handleStartComparison = async () => {
    if (!canStartComparison()) {
      showAlert(t('completeRequiredFields'), 'warning');
      return;
    }

    try {
      setLoading(prev => ({ ...prev, comparison: true }));
      setComparisonError(null);

      const comparisonData = {
        position_id: targetPosition.id || targetPosition.uid,
        comparison_type: comparisonMode,
        ideal_applicant: comparisonMode === '1vsAll' ? idealApplicant : null,
        selected_reports: selectedReports,
        project: project,
        organization,
        language
      };

      const { data } = await api.post('/comparisons', comparisonData, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      if (data.response && data.response.status_response === 'OK') {
        const comparison = data.response.data;
        setCurrentComparison(comparison);
        setCurrentStep('results');

        showAlert(t('comparisonDashboard.comparisonStarted'), 'success');
        startPolling(comparison.id);
      } else {
        throw new Error(data.response?.message || t('comparisonDashboard.comparisonCreateError'));
      }
    } catch (error) {
      console.error('Error al iniciar comparación:', error);
      setComparisonError(error.response?.data?.response?.message || error.message);
      showAlert(t('generalApiError'), 'error');
    } finally {
      setLoading(prev => ({ ...prev, comparison: false }));
    }
  };

  const startPolling = (comparisonId) => {
    const interval = setInterval(async () => {
      try {
        const { data } = await api.get(`/comparisons/${comparisonId}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });

        if (data.response && data.response.status_response === 'OK') {
          const updatedComparison = data.response.data;
          setCurrentComparison(updatedComparison);

          if (updatedComparison.status === 'completado' || updatedComparison.status === 'error') {
            clearInterval(interval);
            setPollingInterval(null);

            if (updatedComparison.status === 'error') {
              setComparisonError(t('processingError'));
            }
          }
        }
      } catch (error) {
        console.error('Error en polling:', error);
      }
    }, 5000);

    setPollingInterval(interval);
  };

  const handleRetryComparison = async () => {
    if (!currentComparison) return;

    try {
      setLoading(prev => ({ ...prev, comparison: true }));
      setComparisonError(null);

      const { data } = await api.post(`/comparisons/${currentComparison.id}/retry`, {}, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      if (data.response && data.response.status_response === 'OK') {
        showAlert(t('retrying'), 'info');
        startPolling(currentComparison.id);
      } else {
        throw new Error(data.response?.message || 'Error al reintentar');
      }
    } catch (error) {
      console.error('Error al reintentar:', error);
      setComparisonError(error.response?.data?.response?.message || error.message);
      showAlert(t('generalApiError'), 'error');
    } finally {
      setLoading(prev => ({ ...prev, comparison: false }));
    }
  };

  const handleNewComparison = () => {
    setCurrentStep('setup');
    setCurrentComparison(null);
    setComparisonError(null);

    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  };

  // Filtrar applicants según los filtros activos
  const applicantsCount = Object.keys(groupedApplicants).length;

  const filteredApplicants = Object.entries(groupedApplicants).reduce((acc, [key, applicant]) => {
    const matchesFilter = !applicantFilter ||
      applicant.applicantName.toLowerCase().includes(applicantFilter.toLowerCase());

    const matchesPosition = !showOnlyTargetPosition || !targetPosition ||
      applicant.reports.some(r => r.originalPositionId === (targetPosition.id || targetPosition.uid));

    if (matchesFilter && matchesPosition) {
      acc[key] = applicant;
    }
    return acc;
  }, {});

  const filteredApplicantsCount = Object.keys(filteredApplicants).length;
  const selectedReportsCount = Object.keys(selectedReports).length;

  const renderUsageLimitMessage = () => {
    const limitText = usageCheck?.limit === -1
      ? t('comparisonDashboard.unlimited')
      : usageCheck?.limit;

    return (
      <Box sx={{ mt: 3, mb: 3 }}>
        <Alert severity="warning">
          <AlertTitle>{t('comparisonDashboard.usageLimitReached')}</AlertTitle>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {t('comparisonDashboard.usageLimitMessage')}
          </Typography>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>{t('comparisonDashboard.currentUsage')}:</strong> {usageCheck.currentUsage} / {limitText} {t('comparisonDashboard.points')}
            </Typography>
            {usageCheck.remaining !== undefined && usageCheck.remaining >= 0 && (
              <Typography variant="body2" color="text.secondary">
                <strong>{t('comparisonDashboard.remaining')}:</strong> {usageCheck.remaining} {t('comparisonDashboard.points')}
              </Typography>
            )}
          </Box>
        </Alert>
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
          {t('comparisonDashboard.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('comparisonDashboard.subTitle')}
        </Typography>
      </Box>

      {/* Tabs Navigation */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} aria-label="comparison tabs">
          <Tab
            icon={<AssignmentIcon />}
            label={t('comparisonDashboard.create')}
            id="comparison-tab-0"
            aria-controls="comparison-tabpanel-0"
            iconPosition="start"
          />
          <Tab
            icon={<DashboardIcon />}
            label={t('comparisonDashboard.dashboard')}
            id="comparison-tab-1"
            aria-controls="comparison-tabpanel-1"
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <Box sx={{ p: 3 }}>
        {/* Tab 0: Crear */}
        {tabValue === 0 && (
          <>
            {/* Vista de Resultados */}
            {currentStep === 'results' ? (
              <Box>
                <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={handleNewComparison}
                    variant="outlined"
                  >
                    {t('comparisonDashboard.newComparison')}
                  </Button>
                  <Typography variant="h5" component="h2">
                    {t('comparisonDashboard.comparisonResults')}
                  </Typography>
                </Box>

                <ComparisonResults
                  comparison={currentComparison}
                  loading={loading.comparison || (currentComparison && currentComparison.status === 'procesando')}
                  error={comparisonError}
                  onRetry={handleRetryComparison}
                  onNewComparison={handleNewComparison}
                />
              </Box>
            ) : (
              /* Vista de Configuración */
              <>
                <Box sx={{ mb: 4 }}>
                  <Stepper activeStep={0} alternativeLabel>
                    <Step>
                      <StepLabel>{t('comparisonDashboard.config')}</StepLabel>
                    </Step>
                    <Step>
                      <StepLabel>{t('comparisonDashboard.results')}</StepLabel>
                    </Step>
                  </Stepper>
                </Box>

                {loadingUsageCheck ? (
                  <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} sx={{ mr: 2 }} />
                    <Typography>{t('comparisonDashboard.checkingUsageLimits')}</Typography>
                  </Box>
                ) : (
                  usageCheck && !usageCheck.canPerform ? (
                    renderUsageLimitMessage()
                  ) : (
                    <ComparisonSetup
                      // Data props
                      positions={positions}
                      targetPosition={targetPosition}
                      groupedApplicants={filteredApplicants}
                      selectedReports={selectedReports}
                      comparisonMode={comparisonMode}
                      idealApplicant={idealApplicant}
                      loading={loading}
                      applicantsCount={filteredApplicantsCount}
                      selectedReportsCount={selectedReportsCount}
                      comparisonError={comparisonError}

                      // Filter props
                      applicantFilter={applicantFilter}
                      showOnlyTargetPosition={showOnlyTargetPosition}
                      totalApplicantsCount={applicantsCount}

                      // Callback props
                      onTargetPositionChange={setTargetPosition}
                      onReportSelection={handleReportSelection}
                      onComparisonModeChange={handleComparisonModeChange}
                      onIdealApplicantSelection={handleIdealApplicantSelection}
                      onStartComparison={handleStartComparison}
                      onApplicantFilterChange={setApplicantFilter}
                      onShowOnlyTargetPositionChange={setShowOnlyTargetPosition}
                      canStartComparison={canStartComparison}
                      formatDate={formatDate}
                    />
                  )
                )}
              </>
            )}
          </>
        )}

        {/* Tab 1: Dashboard */}
        {tabValue === 1 && <ComparisonDashboard />}
      </Box>
    </Box>
  );
}